/**
 * @file BlendPasteModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-08-13
 * @description Confirms what a pasted block of "material name / value" rows will
 *              do before it does it.
 *
 *              The whole point is that nothing is dropped silently. A name the
 *              master list doesn't recognise gets a dropdown to map it, and
 *              mapping it can save the spelling as an alias so the same paste
 *              resolves cleanly next month. Rows left unmapped are shown as
 *              skipped, explicitly, rather than quietly vanishing.
 */

import React, { useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import SearchableDropdown from './SearchableDropdown';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import './BlendCalculator.css';

function PasteModalBody({ rows, setRows, materials, rememberAliases, setRememberAliases }) {
  // Must run inside the modal body: Modal portals on a second render cycle, so
  // a hook called from the parent measures buttons that aren't in the DOM yet.
  useSlideOutButton([rows]);

  const options = useMemo(() => materials.map((m) => ({
    value: m.key,
    label: m.name,
    searchText: [m.name, m.supplier_code, ...(m.aliases || [])].filter(Boolean).join(' ')
  })), [materials]);

  const unmatched = rows.filter((r) => !r.materialKey).length;

  return (
    <div className="blend-paste-body">
      <p className="blend-paste-summary">
        {rows.length} row(s) pasted.{' '}
        {unmatched === 0
          ? 'Every material was recognised.'
          : `${unmatched} name(s) aren't in the material list — map them below or they'll be skipped.`}
      </p>

      <table className="data-table blend-paste-table">
        <thead>
          <tr>
            <th>Use</th>
            <th>Pasted name</th>
            <th>Material</th>
            <th className="num">Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row.name}-${i}`} className={row.materialKey ? '' : 'unmatched'}>
              <td>
                <TickCheckbox
                  id={`paste-use-${i}`}
                  checked={row.use && !!row.materialKey}
                  disabled={!row.materialKey}
                  ariaLabel={`Use ${row.name}`}
                  onChange={() => setRows(rows.map((r, j) =>
                    j === i ? { ...r, use: !r.use } : r))}
                />
              </td>
              <td className="blend-paste-name">{row.name}</td>
              <td>
                <SearchableDropdown
                  options={options}
                  value={row.materialKey}
                  compact
                  placeholder="Not recognised — pick one"
                  searchPlaceholder="Search materials…"
                  onChange={(v) => setRows(rows.map((r, j) =>
                    j === i ? { ...r, materialKey: v, use: true, mappedByHand: true } : r))}
                />
              </td>
              <td className="num">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.some((r) => r.mappedByHand) && (
        <label className="blend-paste-remember">
          <TickCheckbox
            id="paste-remember"
            checked={rememberAliases}
            onChange={() => setRememberAliases(!rememberAliases)}
            ariaLabel="Remember these spellings"
          />
          <span>
            Remember these spellings, so the same paste resolves on its own next time
          </span>
        </label>
      )}
    </div>
  );
}

export default function BlendPasteModal({ pairs, resolved, materials, onCancel, onApply }) {
  const [rows, setRows] = useState(() => pairs.map((p) => {
    const hit = resolved[p.name];
    return {
      name: p.name,
      value: p.value,
      materialKey: hit?.key || null,
      use: !!hit,
      mappedByHand: false
    };
  }));
  const [rememberAliases, setRememberAliases] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    setSaving(true);
    try {
      if (rememberAliases) {
        // Fire and forget: an alias that fails to save costs nothing but a
        // repeat mapping next month, and shouldn't block the paste.
        await Promise.all(rows
          .filter((r) => r.mappedByHand && r.materialKey)
          .map((r) => {
            const material = materials.find((m) => m.key === r.materialKey);
            if (!material) return null;
            return fetch(`${API_URL}/api/blends/materials/${material.id}/aliases`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ alias: r.name })
            }).catch(() => null);
          })
          .filter(Boolean));
      }
      onApply(rows.filter((r) => r.use && r.materialKey));
    } finally {
      setSaving(false);
    }
  };

  const usable = rows.filter((r) => r.use && r.materialKey).length;

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title="Paste materials"
      size="lg"
      className="blend-paste"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onCancel}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={apply} disabled={!usable || saving}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>
                  {saving ? 'progress_activity' : 'content_paste_go'}
                </span>
              </div>
              <span className="btn-label">{saving ? 'Applying…' : `Apply ${usable}`}</span>
            </button>
          </div>
        </div>
      }
    >
      <PasteModalBody
        rows={rows}
        setRows={setRows}
        materials={materials}
        rememberAliases={rememberAliases}
        setRememberAliases={setRememberAliases}
      />
    </Modal>
  );
}
